import axios from "axios";
import { flatTreeBy, pluck, uniqby } from "@/fp";
import {
  ContextLabel,
  FoldersContextLabel,
  InformationType,
  InfoTypeHelper,
  SensitivityLevelHelper,
} from "../models";
import { apiCall } from "./ApiResult.model";

export const get_l0_labels_with_policies = apiCall<void, InformationType[]>(
  "get-l0-labels-with-policies",
  async () => {
    const base_url = "/labels/tree?maxGeneration=0&includePolicy=true";
    const res = await Promise.all([
      axios.get(`${base_url}&labelType=Content_abstract`),
      axios.get(`${base_url}&labelType=Content_label&category=Content`),
    ]);

    return {
      success: true,
      value: res.reduce((acc, r) => {
        return acc.concat(r.data as InformationType[]);
      }, [] as InformationType[]),
    };
  }
);

export const getAllLabels = apiCall<void, InformationType[]>(
  "get-all-labels",
  async () => {
    const res = await axios.get("/label-api/all");
    return {
      success: true,
      value: res.data.filter(
        (t: any) =>
          ["Custom_label", "Content_label"].includes(t.labelType) &&
          t.labelCategory !== "Virtual" &&
          t.generation < 5
      ),
    };
  }
);

export const getAllVirtualLabels = apiCall<void, InformationType[]>(
  "all-virtual-labels",
  async () => {
    const res = await axios.get("/labels/tree?category=Virtual");
    return {
      success: true,
      value: uniqby("id")(res.data),
    };
  }
);

export const get_context_labels = apiCall<void, ContextLabel[]>(
  "all-abstract-labels",
  async () => {
    const [cluster_labels_res, entity_labels_res] = await Promise.all([
      axios.get("/labels/tree?labelType=Cluster_label"),
      axios.get("/labels/tree?labelType=Entity_label"),
    ]);
    const props = pluck("id", "labelCategory", "labelName", "labelType");
    return {
      success: true,
      value: [...cluster_labels_res.data, ...entity_labels_res.data].map(
        props
      ) as ContextLabel[],
    };
  }
);

export const get_internal_external_abstract_labels = apiCall<
  void,
  InformationType[]
>("all-abstract-labels", async () => {
  const res = await axios.get("/labels/tree?labelType=Content_abstract");
  return {
    success: true,
    value: uniqby("id")(res.data).filter((d) =>
      ["Internal Information", "External Information"].includes(d.labelCategory)
    ),
  };
});

export const get_folders_context_labels = apiCall<void, FoldersContextLabel[]>(
  "folders-context-labels.get",
  async () => {
    const res = await axios.get("/labels/tree?labelType=Folder_context");
    return {
      success: true,
      value: uniqby("id")(
        res.data.map((l: any) => {
          return {
            ...l,
            sensitivityLevel: SensitivityLevelHelper.floatToInt(l.sensitivity),
          };
        })
      ),
    };
  }
);

export const getAllContentLabels = apiCall<void, InformationType[]>(
  "all-content-labels",
  async () => {
    const res = await axios.get(
      "/labels/tree?maxGeneration=1&labelType=Content_label&category=Content&includePolicy=true"
    );
    const labelsTree: any[] = res.data;
    const flatLabels = labelsTree
      .map(
        flatTreeBy("nested", (p) => (c) => {
          return {
            ...c,
            parentId: p.id,
          };
        })
      )
      .flat(4)
      .map((l) => {
        if (l.generation === 0) {
          l.labelName = l.labelName.split(" ")[0];
          l.l0Order = InfoTypeHelper.getL0Order(l.code);
        }
        return {
          ...l,
          id: (l.id as string).toLowerCase(),
          parentId: l.abstracts[0]
            ? l.abstracts[0]
            : l.parentId
            ? l.parentId
            : "L0",
          concreteScore: l.concrete,
          shortName: l.labelName.split(" ")[0],
          name: l.labelName,
          sensitivityLevel: SensitivityLevelHelper.floatToInt(l.sensitivity),
          policySection: l.policySection
            ? l.policySection
            : InfoTypeHelper.DEFAULT_POLICIES_SECTION(),
        };
      });
    return {
      success: true,
      value: uniqby("id")(flatLabels),
    };
  }
);
export const getL1sDesc = apiCall<void, { [key: string]: string }>(
  "label-and-insight-desc",
  async () => {
    const res = await axios({
      method: "GET",
      baseURL: "",
      url: "/json/L1Desc.json",
    });
    return {
      success: true,
      // @ts-ignore
      value: res.data.reduce((acc, item) => {
        acc[item.id] = item.description;
        return acc;
      }, {}),
    };
  }
);

export const changeLabelsSensitivity = apiCall<
  Array<{ id: string; sensitivity: number }>,
  number
>("update-label-sensitivity", async (labels) => {
  await axios.patch("/label-api/", labels);
  return {
    success: true,
    value: 200,
  };
});

//#region content labels tree

const has_children = (l: any) => l.nested && l.nested.length > 0;

const get_parent_id = (l: any) => l.abstracts[0] || "L0";

const short_name_by_generation = (name: string, generation: number) => {
  switch (generation) {
    case 0:
      return name.split(" ")[0];
    case 1:
      return name.split(" ").join("_");
    case 2:
    default:
      return name.split(" ").join("__");
  }
};

const set_parent_id_and_code = (
  items: any[],
  code = -1,
  path = [] as string[]
) => {
  for (const item of items) {
    const current_path = [...path, item.id];
    item.concreteScore = item.concrete;
    item.path = current_path;
    if (code > -1) {
      if (item.code != code) {
        item.content_code = item.code;
      }
      item.code = code;
    }
    item.shortName = short_name_by_generation(item.labelName, item.generation);
    item.name = item.labelName;
    if (item.generation === 0) {
      item.labelName = item.labelName.split(" ")[0];
      item.l0Order = InfoTypeHelper.getL0Order(item.code);
    }
    item.sensitivityLevel = SensitivityLevelHelper.floatToInt(item.sensitivity);
    // @ts-ignore
    item.sensitivityTypeName = {
      External: "any",
      Both: "any",
      Internal: "Internal",
    }[item.sensitivityType];
    item.policySection = item.policySection
      ? item.policySection
      : InfoTypeHelper.DEFAULT_POLICIES_SECTION();
    if (has_children(item)) {
      item.parentId = get_parent_id(item);
      item.nested = set_parent_id_and_code(
        uniqby("id")(item.nested),
        item.code,
        [...current_path]
      ).sort((a, b) => a.labelName.localeCompare(b.labelName));
    } else {
      item.parentId = get_parent_id(item);
      if (code > -1) {
        if (item.code != code) {
          item.content_code = item.code;
        }
        item.code = code;
      }
    }
  }
  return items;
};

/**
 * @returns a tree of l0>l1>l2 where each child has a parentId property and the L0 code.
 */
export const get_content_labels_tree = apiCall<void, any>(
  "all-content-labels",
  async () => {
    const res = await axios.get(
      "/labels/tree?maxGeneration=4&labelType=Content_label&category=Content&includePolicy=true"
    );
    const labelsTree: any[] = res.data.filter((l: any) => l.generation === 0);
    return {
      success: true,
      value: set_parent_id_and_code(labelsTree),
    };
  }
);
//#endregion

export const get_adhoc_labels = apiCall<void, any>(
  "all-content-labels",
  async () => {
    const res = await axios.get("/labels/tree?labelType=Adhoc");
    return {
      success: true,
      value: res.data
        .filter((f: any) => f.regulationType === "Privacy")
        .map((d: any) => {
          return {
            ...d,
            concreteScore: d.concrete,
            shortName: short_name_by_generation(d.labelName, d.generation),
            name: d.labelName /* + "_MIP" */,
            labelName: d.labelName /* + "_MIP" */,
            sensitivityLevel: SensitivityLevelHelper.floatToInt(d.sensitivity),
            parentId: InfoTypeHelper.REGULATED_ID(),
            content_code: 8,
            generation: 1,
            code: 7,
          };
        }),
    };
  }
);

export const get_abstract_has_mip_and_PII_labels = apiCall<
  { is_before_date_of_new_piis: boolean },
  any
>("all-content-labels", async ({ is_before_date_of_new_piis }) => {
  let url = "/labels/tree?labelType=Content_abstract&maxGeneration=4";

  if (is_before_date_of_new_piis) {
    url = "/labels/tree?labelType=Content_abstract";
  }

  const res = await axios.get(url);
  const has_mip_labels = res.data
    .filter((f: any) => f.labelCategory === "Has MIP Label")
    .map((d: any) => {
      return {
        id: d.id,
        labelCategory: d.labelCategory,
        labelType: d.labelType,
        labelName: d.labelName,
        sensitivity: d.sensitivity,
        sensitivityLevel: SensitivityLevelHelper.floatToInt(d.sensitivity),
        code: d.code,
        generation: d.generation,
        parentId: d.infoTypeCategoryId,
        regulationType: d.regulationType,
      };
    });

  const items = res.data
    .find((d: any) => d.labelName == "Regulated Information")
    .nested.filter(
      (d: any) =>
        d.labelCategory == "Regulations" || d.labelCategory == "Content"
    );
  let pfi_pci_pii = res.data.filter((d: any) =>
    ["Contains PFI", "Contains PCI", "Contains PII"].includes(d.labelCategory)
  );

  pfi_pci_pii.forEach((l: any) => {
    l.infoTypeCategoryId = "a9383d32-7694-4d84-b37c-79afb945505b";
  });
  if (is_before_date_of_new_piis) {
    pfi_pci_pii = [];
  }

  /*  console.log(
    "AA pfi_pci_pii:",
    pfi_pci_pii.map((d: any) => d.labelName)
  ); */

  const PII_labels = set_parent_id_and_code([...items, ...pfi_pci_pii], 7, [
    InfoTypeHelper.REGULATED_ID(),
  ]);
  /*   console.log(
    "AA PII_labels:",
    PII_labels.map((d: any) => d.labelName)
  ); */
  return {
    success: true,
    value: [has_mip_labels, PII_labels],
  };
});

export const get_dynamics_labels = apiCall<void, InformationType[]>(
  "dynamics-labels",
  async () => {
    const res = await axios.get("/labels/tree?labelType=Context&category=CRM");
    const dynamics_labels = res.data.map((d: any) => {
      return {
        id: d.id,
        labelCategory: d.labelCategory,
        labelType: d.labelType,
        labelName: d.labelName,
        sensitivity: d.sensitivity,
        sensitivityLevel: SensitivityLevelHelper.floatToInt(d.sensitivity),
        code: d.code,
        generation: d.generation,
        parentId: d.infoTypeCategoryId,
        regulationType: d.regulationType,
      };
    });

    return {
      success: true,
      value: dynamics_labels,
    };
  }
);
